/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper,
  Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentTemplatesAPI, documentsAPI, filesAPI, signaturesAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { formatDate } from '../../utils/helpers'
import { useNotify } from '../common/NotificationProvider'
import PdfPreview from './PdfPreview'

const STATUS = {
  SENT: { label: 'Waiting for signatures', color: 'info' },
  COMPLETED: { label: 'Signed by everyone', color: 'success' },
  DECLINED: { label: 'Declined', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'default' },
  EXPIRED: { label: 'Expired', color: 'default' },
}
const SIGNER = {
  PENDING: { label: 'To sign', color: 'warning' },
  WAITING: { label: 'Waiting for turn', color: 'default' },
  SIGNED: { label: 'Signed', color: 'success' },
  DECLINED: { label: 'Declined', color: 'error' },
}

/** On a record: make PDFs from templates, send PDFs for e-signature, follow them. */
const RecordDocuments = ({ record, canEdit }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [making, setMaking] = useState(false)
  const [sending, setSending] = useState(null)   // { attachmentId } or {}
  const templates = useQuery({ queryKey: [tenantId, 'document-templates', record.entityType], queryFn: () => documentTemplatesAPI.list(record.entityType) })
  const requestsKey = [tenantId, 'records', 'signatures', record.id]
  const requests = useQuery({ queryKey: requestsKey, queryFn: () => signaturesAPI.list(record.id), refetchInterval: 30_000 })
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: requestsKey })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'files', record.id] })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity', record.id] })
  }
  const act = useMutation({
    mutationFn: ({ kind, id }) => (kind === 'cancel' ? signaturesAPI.cancel(record.id, id) : signaturesAPI.remind(record.id, id)),
    onSuccess: (_, { kind }) => { notify(kind === 'cancel' ? 'Signature request cancelled' : 'New links emailed'); refresh() },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const list = requests.data || []
  const hasTemplates = (templates.data || []).length > 0
  if (!canEdit && list.length === 0) return null

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: list.length ? 2 : 0 }}>
        <Box>
          <Typography variant="subtitle2">Documents & signatures</Typography>
          {list.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {hasTemplates ? 'Make a PDF from a template, or send one of this record’s PDFs to be signed.' : 'Send one of this record’s PDFs to be signed. Admins can add document templates in Settings.'}
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {hasTemplates && <Button size="small" startIcon={<NoteAddOutlinedIcon />} onClick={() => setMaking(true)}>Make a document</Button>}
            <Button size="small" startIcon={<DrawOutlinedIcon />} onClick={() => setSending({})}>Send for signature</Button>
          </Stack>
        )}
      </Stack>
      <Stack spacing={1.5}>
        {list.map((r) => (
          <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{r.documentName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Sent {formatDate(r.createdOn)}{r.status === 'SENT' ? ` · open until ${formatDate(r.expiresAt)}` : ''}{r.inOrder ? ' · in order' : ''}
                </Typography>
              </Box>
              <Chip size="small" color={STATUS[r.status]?.color} label={STATUS[r.status]?.label || r.status} sx={{ alignSelf: { sm: 'flex-start' } }} />
            </Stack>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1 }}>
              {r.signers.map((s) => (
                <Tooltip key={s.id} title={s.signedAt ? `Signed ${formatDate(s.signedAt)}` : s.declineReason ? `Declined: ${s.declineReason}` : s.viewedAt ? `Opened ${formatDate(s.viewedAt)}` : s.email}>
                  <Chip size="small" variant="outlined" color={SIGNER[s.status]?.color} label={`${s.name} · ${SIGNER[s.status]?.label || s.status}`} />
                </Tooltip>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {r.signedAttachmentId && (
                <Button size="small" onClick={() => filesAPI.content(record.id, r.signedAttachmentId, true).then((b) => window.open(URL.createObjectURL(b), '_blank'))}>
                  Open signed copy
                </Button>
              )}
              {canEdit && r.status === 'SENT' && r.emailed && (
                <Button size="small" onClick={() => act.mutate({ kind: 'remind', id: r.id })} disabled={act.isPending}>Remind</Button>
              )}
              {canEdit && r.status === 'SENT' && (
                <Button size="small" color="inherit" onClick={() => act.mutate({ kind: 'cancel', id: r.id })} disabled={act.isPending}>Cancel</Button>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>
      {making && (
        <MakeDocumentDialog record={record} templates={templates.data} onClose={() => setMaking(false)}
          onSaved={(attachment) => { refresh(); setMaking(false); setSending({ attachmentId: attachment.id }) }} />
      )}
      {sending && <SendForSignatureDialog record={record} initialAttachmentId={sending.attachmentId} onClose={() => setSending(null)} onSent={refresh} />}
    </Paper>
  )
}

/** Template → preview → saved to the record's files (then optionally on to signing). */
const MakeDocumentDialog = ({ record, templates, onClose, onSaved }) => {
  const notify = useNotify()
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'))
  const [templateId, setTemplateId] = useState(templates[0]?.id || '')
  const preview = useQuery({
    queryKey: ['document-preview', record.id, templateId],
    queryFn: () => documentsAPI.preview(record.id, templateId),
    enabled: !!templateId,
    retry: false,
    gcTime: 0,
  })
  const save = useMutation({
    mutationFn: () => documentsAPI.generate(record.id, templateId),
    onSuccess: (attachment) => { notify(`Saved “${attachment.fileName}” to the record's files`); onSaved(attachment) },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Make a document</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          {(preview.error || save.error) && <Alert severity="error">{blobError(preview.error) || getErrorMessage(save.error)}</Alert>}
          {preview.isLoading ? <Typography color="text.secondary">Making the preview…</Typography> : <PdfPreview blob={preview.data} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!preview.data || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save to files and send for signature…'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** A PDF of the record + who signs → links by email (or to copy when email is off). */
const SendForSignatureDialog = ({ record, initialAttachmentId, onClose, onSent }) => {
  const tenantId = useTenantKey()
  const notify = useNotify()
  const files = useQuery({ queryKey: [tenantId, 'records', 'files', record.id], queryFn: () => filesAPI.list(record.id) })
  const pdfs = (files.data || []).filter((f) => f.contentType === 'application/pdf')
  const [attachmentId, setAttachmentId] = useState(initialAttachmentId || '')
  const [signers, setSigners] = useState([{ name: '', email: '' }])
  const [message, setMessage] = useState('')
  const [inOrder, setInOrder] = useState(false)
  const [days, setDays] = useState(14)
  const [links, setLinks] = useState(null)
  const chosen = attachmentId || pdfs[0]?.id || ''
  const send = useMutation({
    mutationFn: () => signaturesAPI.send(record.id, {
      attachmentId: chosen, signers: signers.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
      message: message.trim() || undefined, inOrder, expiresInDays: days,
    }),
    onSuccess: (request) => {
      onSent()
      if (request.signers.some((s) => s.signingUrl)) {
        setLinks(request.signers)
      } else {
        notify(`Sent to ${request.signers.map((s) => s.name).join(', ')}`)
        onClose()
      }
    },
  })
  const setSigner = (i, key) => (e) => setSigners((list) => list.map((s, j) => (j === i ? { ...s, [key]: e.target.value } : s)))
  const ready = chosen && signers.every((s) => s.name.trim() && /\S+@\S+\.\S+/.test(s.email.trim()))

  if (links) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send these links</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Email isn’t set up here, so send each person their own link (by email, WhatsApp…). Links are personal: whoever has one can sign as that person.
            {inOrder ? ' Each link works once the person before has signed.' : ''}
          </Alert>
          <Stack spacing={1.5}>
            {links.map((s) => (
              <TextField key={s.id} label={`${s.name} (${s.email})`} value={s.signingUrl} InputProps={{ readOnly: true, endAdornment: (
                <IconButton size="small" aria-label={`Copy link for ${s.name}`} onClick={() => navigator.clipboard.writeText(s.signingUrl).then(() => notify('Link copied'))}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ) }} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={onClose}>Done</Button></DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); send.mutate() } }}>
      <DialogTitle>Send for signature</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {send.error && <Alert severity="error">{getErrorMessage(send.error)}</Alert>}
          {files.isSuccess && pdfs.length === 0 ? (
            <Alert severity="info">This record has no PDF yet. Upload one in Files, or make one from a template.</Alert>
          ) : (
            <TextField select label="Document" value={chosen} onChange={(e) => setAttachmentId(e.target.value)}>
              {pdfs.map((f) => <MenuItem key={f.id} value={f.id}>{f.fileName}</MenuItem>)}
            </TextField>
          )}
          <Typography variant="subtitle2">Who signs</Typography>
          {signers.map((s, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              {inOrder && <Typography variant="body2" color="text.secondary" sx={{ minWidth: 18 }}>{i + 1}.</Typography>}
              <TextField size="small" label="Name" required value={s.name} onChange={setSigner(i, 'name')} sx={{ flex: 1 }} inputProps={{ maxLength: 100 }} />
              <TextField size="small" label="Email" type="email" required value={s.email} onChange={setSigner(i, 'email')} sx={{ flex: 1.3 }} />
              <IconButton aria-label="Remove signer" disabled={signers.length === 1} onClick={() => setSigners((list) => list.filter((_, j) => j !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button size="small" startIcon={<AddIcon />} disabled={signers.length >= 10} onClick={() => setSigners((list) => [...list, { name: '', email: '' }])}>
              Add a signer
            </Button>
          </Box>
          {signers.length > 1 && (
            <FormControlLabel control={<Checkbox checked={inOrder} onChange={(e) => setInOrder(e.target.checked)} />}
              label="Sign in this order (each person gets the link after the one before has signed)" />
          )}
          <TextField label="Message (optional)" multiline minRows={2} value={message} onChange={(e) => setMessage(e.target.value)} inputProps={{ maxLength: 2000 }} />
          <TextField select label="Links work for" value={days} onChange={(e) => setDays(e.target.value)} sx={{ maxWidth: 200 }}>
            {[3, 7, 14, 30, 60].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Each person reads the PDF, types their name and agrees to sign electronically. When everyone has signed, the signed copy with a
            signature certificate (who, when, from where) is saved here and emailed to everyone.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={!ready || send.isPending}>{send.isPending ? 'Sending…' : 'Send'}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Errors of blob requests arrive as Blobs; the dialog shows a plain message. */
export const blobError = (error) => (error ? (error.response?.status === 400 ? 'This template can’t be used for this record' : getErrorMessage(error)) : null)

export default RecordDocuments
